import React from "react";
import LegalLayout from "./LegalLayout";
import { LINKS } from "../../lib/constants";

export default function TermsAndConditions() {
  return (
    <LegalLayout title="Terms & Conditions of Sale" lastUpdated="July 2026">
      <p>
        These Standard Terms and Conditions of Business ("Terms") govern the supply of paid
        services by ProfitMIND Ltd to business customers. They apply to the Money Flow Strategy
        Session, the Financial Leadership Advisory Programme, and any paid subscription or one-off
        purchase of <strong>forecastly</strong> (a trading name of ProfitMIND Ltd). They do{" "}
        <strong>not</strong> apply to consumer sales — these are business-to-business terms.
      </p>

      <h2>1. Interpretation</h2>
      <p>The following definitions apply in these Terms:</p>
      <ul>
        <li><strong>"the Customer"</strong> — the company, firm, sole trader or individual purchasing services from the Supplier in the course of their business.</li>
        <li><strong>"the Supplier"</strong> — ProfitMIND Ltd (company number 09918255), whose registered office is 4 Capricorn Centre, Cranes Farm Road, Basildon, Essex, SS14 3JJ, United Kingdom. The Supplier also trades as <strong>forecastly</strong>.</li>
        <li><strong>"the Services"</strong> — the financial strategy, advisory, mentorship and software services that the Supplier agrees to provide to the Customer, as described on the relevant offer page or order confirmation.</li>
        <li><strong>"Service Specification"</strong> — the description, deliverables and timeline for the Services as set out on the relevant landing page, booking confirmation or proposal.</li>
        <li><strong>"Intellectual Property Rights"</strong> — patents, copyrights, trade marks, design rights, database rights, trade secrets, methodologies (including the Strategic Flow™ Method, Clarity Flow™, Strategy Flow™, Cash Confidence Flow™, CEO Decision Flow™ and Vision-Driven Growth Flow™), domain names, goodwill and all similar or equivalent rights worldwide, whether registered or unregistered.</li>
        <li><strong>"Force Majeure Event"</strong> — any event beyond the reasonable control of either party, including serious illness, bereavement, extreme weather, fire, flood, pandemic, civil unrest, war, internet outage, hosting-provider outage, third-party software failure, or compliance with law or government order.</li>
        <li><strong>"Working Day"</strong> — a day other than a Saturday, Sunday or English public holiday.</li>
        <li><strong>"these Terms"</strong> — these Terms and Conditions of Sale, as updated from time to time.</li>
      </ul>
      <p>Headings are for convenience only and do not affect interpretation. Words in the singular include the plural. "Including" and "for example" are illustrative and non-exhaustive.</p>

      <h2>2. Basis of the contract</h2>
      <p>
        The Supplier contracts on these Terms only. Acceptance by the Supplier of an order from
        a Customer is on these Terms and overrides any conflicting terms the Customer attempts to
        impose (whether in a purchase order or otherwise).
      </p>
      <p>
        Any quotation given by the Supplier is valid for 30 days from the date of issue and does
        not constitute an offer. No contract is concluded until the Supplier has issued written
        confirmation of the booking (which may be the automated TidyCal / Stripe receipt) or the
        Customer has paid in full.
      </p>
      <p>Any variations to these Terms must be agreed in writing by an authorised representative of the Supplier.</p>

      <h2>3. The Services</h2>
      <h3>3.1 Money Flow Strategy Session</h3>
      <p>
        A focused 90-minute 1:1 strategy session delivered remotely (Zoom or equivalent),
        accompanied by a personalised "Clarity Map", a 30/60-day action plan, and recording &amp;
        notes. Approximately 60 days after the session the Supplier offers a complimentary 30-minute
        accountability check-in.
      </p>

      <h3>3.2 Financial Leadership Advisory Programme</h3>
      <p>
        A high-touch 3 to 12-month 1:1 mentorship programme based on the Strategic Flow™ Method,
        with regular strategy calls, asynchronous support (email / messaging), action plans, and
        quarterly forecasting check-ins. Free access to forecastly is included for the duration
        of the engagement.
      </p>

      <h3>3.3 forecastly (software)</h3>
      <p>
        Access to the forecastly cashflow forecasting software, on the one-off or subscription
        plan selected at the time of purchase. The software is provided "as is" as a planning aid
        — it is not a substitute for professional accounting, tax or legal advice.
      </p>

      <h3>3.4 Nature of advice</h3>
      <p>
        The Services include strategic guidance and mentorship. They <strong>do not</strong>{" "}
        constitute regulated financial advice within the meaning of the Financial Services and
        Markets Act 2000, nor do they replace the role of the Customer's own accountant, tax
        adviser or solicitor. The Customer remains responsible for its own business decisions and
        for taking qualified professional advice where appropriate.
      </p>

      <h3>3.5 Performance dates</h3>
      <p>
        The Supplier will use reasonable endeavours to perform the Services on the dates agreed
        with the Customer. Dates for performance are estimates and time is not of the essence,
        except where expressly agreed in writing.
      </p>

      <h2>4. Price and payment</h2>
      <p>
        Prices for the Services are stated on the relevant offer page at the time of purchase.
        ProfitMIND Ltd is <strong>not currently VAT registered</strong>, so all prices are{" "}
        <strong>inclusive of VAT</strong> (i.e. no VAT is added). Should ProfitMIND Ltd become VAT
        registered in the future, prices will be updated accordingly and the Customer will be
        notified before any change takes effect. Payment is taken in full at the point of booking,
        unless a payment plan is expressly offered and agreed in writing.
      </p>
      <p>
        Payments are processed by Stripe. Where a recurring subscription is purchased, the
        Customer authorises Stripe to charge the saved payment method on each renewal date.
      </p>
      <p>
        Where the Customer's payment fails, the Supplier may suspend access to the Services until
        the outstanding balance has been settled. The Supplier reserves the right to charge
        interest on overdue invoices at the statutory rate under the Late Payment of Commercial
        Debts (Interest) Act 1998.
      </p>

      <h2>5. Cancellation, rescheduling and refunds</h2>

      <h3>5.1 Money Flow Strategy Session</h3>
      <p>
        The Customer may reschedule a session up to <strong>48 hours</strong> before its start
        time at no cost. Cancellations made more than 7 days before the session are eligible for a
        refund (less a £25 administration fee). Cancellations within 7 days are non-refundable but
        may be transferred once to a new date subject to availability.
      </p>

      <h3>5.2 Financial Leadership Advisory Programme</h3>
      <p>
        The Advisory Programme is a committed engagement. Once the programme has started, fees are
        non-refundable. If the Customer has paid in full and needs to pause for genuine medical or
        compassionate reasons, the parties will work together in good faith to reschedule.
      </p>

      <h3>5.3 forecastly subscriptions</h3>
      <p>
        Subscriptions auto-renew unless cancelled before the next renewal date. Cancellation can
        be made from inside the application or via the Stripe customer portal. Cancellation takes
        effect at the end of the current billing period — the Supplier does not pro-rate or refund
        partial periods. One-off purchases are non-refundable once access has been granted, except
        as required by law.
      </p>

      <h3>5.4 Right to cancel by the Customer</h3>
      <p>
        Without prejudice to any specific cancellation right above, the Customer may not cancel a
        contract without the written consent of the Supplier. Where consent is given, the Customer
        shall indemnify the Supplier against any losses, costs and expenses arising from the
        cancellation, unless otherwise agreed in writing.
      </p>

      <h2>6. The Customer's responsibilities</h2>
      <p>The Customer agrees to:</p>
      <ul>
        <li>Provide accurate information and any documents or data reasonably required to deliver the Services.</li>
        <li>Attend sessions on time and engage in good faith.</li>
        <li>Treat all sessions, materials, frameworks, dashboards and shared content as confidential.</li>
        <li>Make its own informed business decisions — the Supplier provides strategic guidance, not directives.</li>
        <li>Keep its login credentials secure and not share access to forecastly with any third party.</li>
      </ul>

      <h2>7. Intellectual Property Rights</h2>
      <p>
        All Intellectual Property Rights in the Services, including all frameworks, methodologies,
        templates, dashboards, written content and software code, remain the sole property of the
        Supplier (or its licensors). This includes, without limitation, the brand names{" "}
        <strong>ProfitMIND</strong> and <strong>forecastly</strong>, the forecastly cashflow
        forecasting software (its source code, database schema, UI design and user-experience flows),
        the Strategic Flow™ Method and its five constituent frameworks — Clarity Flow™, Strategy
        Flow™, Cash Confidence Flow™, CEO Decision Flow™ and Vision-Driven Growth Flow™. Trade
        mark applications are pending in respect of "ProfitMIND" and "forecastly". The Customer
        is granted a non-exclusive, non-transferable, royalty-free licence to use the materials
        supplied during the engagement <em>solely for the purpose of its own business</em> and only
        for so long as the engagement is active and fees remain paid.
      </p>
      <p>
        The Customer retains ownership of the business and financial data it inputs into
        forecastly. The Customer grants the Supplier a limited licence to host, process and
        display that data for the sole purpose of providing the Services.
      </p>
      <p>The Customer shall not resell, sublicense, reverse-engineer or use the Services to develop a competing offering.</p>

      <h2>8. Confidentiality</h2>
      <p>
        Both parties shall keep confidential any non-public information disclosed during the
        engagement, including financial figures, business plans, personal circumstances and
        proprietary frameworks. This obligation survives termination of the engagement
        indefinitely. The obligation does not apply to information that is (a) already in the
        public domain, (b) required to be disclosed by law, or (c) independently developed without
        reference to the other party's confidential information.
      </p>

      <h2>9. Data protection</h2>
      <p>
        Each party shall comply with its respective obligations under the UK GDPR and the Data
        Protection Act 2018. The Supplier's processing of personal data is described in the{" "}
        <a href="/privacy-policy">Privacy Policy</a>. Where the Customer uploads financial data into
        forecastly, the Customer remains the controller of that data and the Supplier acts as a
        processor on the Customer's behalf.
      </p>

      <h2>10. Results disclaimer</h2>
      <p>
        The Supplier is passionate about the Customer's results — but every business is different.
        The Supplier <strong>does not guarantee</strong> specific revenue, profit, growth or
        outcome metrics. The results the Customer achieves depend on many factors outside the
        Supplier's control, including the Customer's own implementation, market conditions, and
        the accuracy of the figures it inputs into forecastly.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        Nothing in these Terms limits or excludes the Supplier's liability for death or personal
        injury caused by its negligence, for fraud, or for any other liability which cannot
        lawfully be excluded.
      </p>
      <p>Subject to the above:</p>
      <ul>
        <li>The Supplier shall not be liable for any indirect, consequential, special or punitive losses, including loss of profit, loss of revenue, loss of business opportunity, loss of anticipated savings, loss of data, or loss of reputation.</li>
        <li>The Supplier's total aggregate liability arising under or in connection with the Services, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, shall not exceed the fees actually paid by the Customer to the Supplier in the twelve (12) months immediately preceding the event giving rise to the claim.</li>
        <li>The Supplier shall not be liable for any losses arising from the Customer's reliance on AI-generated narrative output produced inside forecastly — such output is provided as a suggestion and should be sense-checked before any decision is taken.</li>
      </ul>

      <h2>12. Termination</h2>
      <p>The Supplier may terminate the contract immediately, without refund, if the Customer:</p>
      <ul>
        <li>commits a material breach of these Terms which is incapable of remedy, or which is not remedied within 14 days of written notice;</li>
        <li>fails to pay any sum due to the Supplier on the due date;</li>
        <li>behaves in a manner that is threatening, abusive or otherwise unreasonable towards the Supplier or its staff;</li>
        <li>becomes insolvent, enters administration, or has a winding-up petition presented against it.</li>
      </ul>
      <p>On termination, any unpaid fees remain due and payable in full.</p>

      <h2>13. Force Majeure</h2>
      <p>
        Neither party shall be liable for failure or delay in performance caused by a Force
        Majeure Event. The affected party shall notify the other in writing as soon as reasonably
        practicable and use reasonable endeavours to resume performance. Where possible, sessions
        will be rescheduled rather than cancelled.
      </p>

      <h2>14. Assignment</h2>
      <p>
        The Customer shall not assign, transfer or sublicense any of its rights or obligations
        under these Terms without the prior written consent of the Supplier. The Supplier may
        assign its rights and obligations to any successor in business without prior consent.
      </p>

      <h2>15. Notices</h2>
      <p>
        All notices under these Terms shall be in writing and sent by email to{" "}
        <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a> (for notices to the Supplier) or to the
        email address most recently provided by the Customer. Notices are deemed received on the
        same Working Day if sent before 17:00 UK time, or the next Working Day if sent after.
      </p>

      <h2>16. Entire agreement</h2>
      <p>
        These Terms, together with the relevant offer page, booking confirmation and the{" "}
        <a href="/privacy-policy">Privacy Policy</a> and <a href="/cookie-policy">Cookie Policy</a>,
        constitute the entire agreement between the parties and supersede any prior agreement or
        representations.
      </p>

      <h2>17. Third-party rights</h2>
      <p>
        A person who is not a party to these Terms has no rights under the Contracts (Rights of
        Third Parties) Act 1999 to enforce any of these Terms.
      </p>

      <h2>18. Severability</h2>
      <p>
        If any provision of these Terms is held by a court to be invalid or unenforceable, the
        remainder of these Terms shall remain in full force and effect, and the parties shall
        negotiate in good faith to replace the invalid provision with a valid one which most
        closely reflects the original intention.
      </p>

      <h2>19. Governing law and jurisdiction</h2>
      <p>
        These Terms and any dispute or claim arising out of or in connection with them shall be
        governed by and construed in accordance with the laws of England and Wales. The parties
        irrevocably agree that the courts of England and Wales shall have exclusive jurisdiction
        to settle any such dispute or claim.
      </p>

      <h2>20. Contact</h2>
      <p>
        For any question about these Terms — including refunds, complaints or anything that didn't
        sit right — email <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>. We aim to reply
        within 2 Working Days.
      </p>
    </LegalLayout>
  );
}
